<div (click)="toggleForm()" class="h-full">
  <ng-content></ng-content>
</div>
<section
  role="dialog"
  *ngIf="show"
  aria-modal="true"
  aria-labelledby="modal-title"
  class="inset-0 z-10 fixed overflow-y-auto"
>
  <div class="flex h-screen text-center px-4 items-center justify-center">
    <div
      aria-hidden="true"
      class="bg-gray-500 bg-opacity-75 inset-0 transition-opacity fixed"
    ></div>

    <form
      class="bg-white rounded-lg flex flex-col h-5/6 shadow-xl text-left w-full transform transition-all sm:max-w-screen-sm sm:my-8 relative"
    >
      <app-loading-indicator *ngIf="loading"></app-loading-indicator>

      <!-- Step 1: Session and Semester Selection -->
      <div *ngIf="!sessionAndSemesterSelected" class="flex flex-col h-full">
        <header class="border-b bg-gray-50 p-8">
          <h1 id="modal-title" class="text-xl font-semibold pb-4">Course Registration</h1>
          <p class="text-sm pb-4">Please select session and semester to continue</p>

          <!-- Display error message for session/semester selection if any -->
          <div *ngIf="notification.message" class="flex-grow overflow-y-auto mb-4">
            <app-no-content
              [icon]="'/assets/images/error-message.png'"
              [caption]="notification.message"
            ></app-no-content>
          </div>

          <div *ngIf="!notification.message" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Session</label>
              <select
                [(ngModel)]="session"
                name="session"
                class="p-2 border rounded-md w-full"
              >
                <option value="" disabled selected>Select Session</option>
                <option *ngFor="let session of sessions" [value]="session">
                  {{ session }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Semester</label>
              <select
                [(ngModel)]="semester"
                name="semester"
                class="p-2 border rounded-md w-full"
              >
                <option value="" disabled selected>Select Semester</option>
                <option *ngFor="let semester of semesters" [value]="semester">
                  {{ semester }}
                </option>
              </select>
            </div>
          </div>
        </header>

        <div class="flex-grow">
          <!-- Empty flex-grow div to push footer to bottom -->
        </div>

        <footer class="border-t bg-gray-50 p-6 flex justify-between">
          <app-button-primary
            [outline]="true"
            (click)="toggleForm()"
          >
            Cancel
          </app-button-primary>

          <app-button-primary
            (click)="proceedToCourseSelection()"
            [disabled]="!session || !semester || notification.error"
          >
            Proceed
          </app-button-primary>
        </footer>
      </div>

      <!-- Step 2: Course Selection - Only shown after session and semester are selected -->
      <div *ngIf="sessionAndSemesterSelected" class="flex flex-col h-full">
        <header class="border-b bg-gray-50 p-8 pb-4 flex-shrink-0">
          <div class="flex justify-between items-center mb-4">
            <h1 class="text-lg font-medium">
              Courses for {{ session }} - {{ semester }}
            </h1>
            <button 
              (click)="backToSessionSelection()" 
              class="text-primary flex items-center text-sm"
            >
              <span class="material-icons text-sm mr-1">arrow_back</span>
              Change Selection
            </button>
          </div>

          <nav
            class="border-primary flex border-b-2 py-1 gap-4 justify-between items-center"
          >
            <input
              name="search"
              [(ngModel)]="filter"
              placeholder="Search for a course"
              class="bg-transparent w-full p-1 focus:outline-none"
            />
            <button class="text-primary material-icons">search</button>
          </nav>
        </header>

        <section *ngIf="skeletonLoader" class="flex-grow p-8 overflow-y-auto">
          <div
            *ngFor="let i of [1, 2, 3, 4, 5, 6]"
            class="border rounded-md bg-gray-300 h-12 mb-2 animate-pulse"
          ></div>
        </section>

        <ng-template [ngIf]="!skeletonLoader">
          <section
            class="flex-grow overflow-y-auto"
            *ngIf="notification.message || filterCourses().length < 1"
          >
            <app-no-content
              [icon]="
                notification.message ? '/assets/images/error-message.png' : ''
              "
              [caption]="
                notification.message ||
                (filter ? 'Nothing found for: ' + filter : 'No courses found')
              "
            ></app-no-content>
          </section>

          <section
            *ngIf="!notification.message && filterCourses().length > 0"
            class="flex flex-col flex-grow p-8 pt-4 gap-4 overflow-y-auto"
          >
            <label
              *ngFor="let course of filterCourses()"
              class="rounded-md border-2 p-4 transform duration-200 justify-between group hover:bg-primary-lighter hover:border-primary hover:scale-105"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div
                    class="font-medium text-base pb-4 capitalize group-hover:text-primary"
                  >
                    {{ course.CourseTitle }}
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2">
                    <div
                      class="flex text-xs text-gray-500 gap-2 uppercase hover:border-primary"
                    >
                      <div class="whitespace-nowrap">
                        {{ course.CourseCode }}
                      </div>
                      &bullet;
                      <div class="whitespace-nowrap">
                        {{ course.CourseUnits }} UNITS
                      </div>
                      &bullet;
                      <div class="whitespace-nowrap">
                        {{ course?.Compulsory ? "Compulsory" : "Elective" }}
                      </div>
                      <div *ngIf="course?.SIWES" class="whitespace-nowrap">
                        &bullet; {{ course?.SIWES ? "SIWES" : "" }}
                      </div>
                    </div>
                  </div>
                </div>

                <input
                  [name]="course.CourseCode"
                  [id]="course.CourseCode"
                  (click)="clickCourse(course.CourseCode, course.Compulsory, course.SIWES)"
                  [checked]="payload.includes(course.CourseCode)"
                  [disabled]="isSiwesSelected() && !course.SIWES"
                  type="checkbox"
                />
              </div>
            </label>
          </section>
        </ng-template>

        <footer class="border-t bg-gray-50 p-6 flex justify-between flex-shrink-0">
          <app-button-primary
            [outline]="true"
            (click)="toggleForm()"
          >
            Cancel
          </app-button-primary>

          <app-button-primary
            (click)="saveCourses()"
            [disabled]="loading || !payload.length || notification.error"
          >
            Save
          </app-button-primary>
        </footer>
      </div>
    </form>
  </div>
</section>